import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
//import AxiosInstance from "./Axios";
import {
  MaterialReactTable,
  useMaterialReactTable,
} from "material-react-table";
import { Add as AddIcon, Remove as RemoveIcon } from "@mui/icons-material";
import { Box, Typography, useMediaQuery, useTheme } from "@mui/material";

import ArbMonitor from "../ArbMonitor";
import TradesTable from "../table/TradesTable";
import PositionsTable from "../table/PositionsTable";

// Import modular UI components
import { CardWrapper, Section, TabNavigation } from "../ui";
import { PositionsDataContext } from "../../context/PositionsDataContext";
import {
  getPnLTableColumns,
  getPnlTableColumnsEthan1,
} from "../table/columnsConfig/pnlTable";
import { getTradeColumnsEthan } from "../table/columnsConfig/trades";
import {
  getCommonBodyCellProps,
  getCommonHeadCellProps,
} from "../table/columnsConfig/commonUtils";

const MAB_PNL = () => {
  // ===== UTILITY FUNCTIONS =====
  const checkAndClearStorageIfNewDay = () => {
    const lastVisitDate = localStorage.getItem("mabLastVisitDate");
    const currentDate = new Date().toDateString();

    if (lastVisitDate !== currentDate) {
      // Clear all localStorage items
      localStorage.removeItem("mabTradesTableGlobalFilter");
      localStorage.removeItem("mabTradesTableSorting");
      localStorage.removeItem("mabTradesTableScrollPosition");
      localStorage.removeItem("mabPositionsTableGlobalFilter");
      localStorage.removeItem("mabPositionsTableSorting");
      localStorage.removeItem("mabPositionsTableScrollPosition");
      localStorage.removeItem("mabPnLTableExpandedRows");
      localStorage.removeItem("mabStrategyPnLTableExpandedRows");
      localStorage.removeItem("mabSecurityPnLTableExpandedRows");
      localStorage.removeItem("mabTab1ScrollPosition");
      localStorage.removeItem("mabTab2ScrollPosition");
      localStorage.removeItem("mabTab3ScrollPosition");
      localStorage.removeItem("positionsTableScrollTop");
      localStorage.removeItem("arbMonitorTableScrollTop");
      localStorage.removeItem("tradesTableEthanScrollTop");
      localStorage.removeItem("activeTab");
      localStorage.removeItem("mabPnlFilter");
      localStorage.removeItem("positionsFilter");

      // Set the new visit date
      localStorage.setItem("mabLastVisitDate", currentDate);
    }
  };

  // ===== STATE =====
  const theme = useTheme();
  const { datasets } = useContext(PositionsDataContext);
  const isDesktop = useMediaQuery(theme.breakpoints.up("md"));

  // Refs
  const pnlTableContainerRef = useRef(null);
  const positionsTableContainerRef = useRef(null);
  const arbMonitorTableContainerRef = useRef(null);
  const tradesTableEthanContainerRef = useRef(null);

  // Data states
  const [allData, setAllData] = useState([]);
  const [maPositions, setMaPositions] = useState([]);
  const [tradedData, setTradedData] = useState([]);
  const [arb_monitor_data, setArbMonitorData] = useState([]);
  const [activeTab, setActiveTab] = useState("tab1");

  // Filter and sorting states
  const [positionsSorting, setPositionsSorting] = useState(() => {
    const savedSorting = localStorage.getItem("mabPositionsTableSorting");
    return savedSorting
      ? JSON.parse(savedSorting)
      : [{ id: "StrategyName", desc: false }];
  });

  const [positionsScrollPosition, setPositionsScrollPosition] = useState(() => {
    return (
      parseInt(localStorage.getItem("mabPositionsTableScrollPosition"), 10) || 0
    );
  });

  const [positionsFilter, setPositionsFilter] = useState(
    () => localStorage.getItem("mabPositionsTableGlobalFilter") || "",
  );

  const [mabTradesGlobalFilter, setMabTradesGlobalFilter] = useState(() => {
    const val = localStorage.getItem("mabTradesTableGlobalFilter");
    return val === null || val === "undefined" ? "" : val;
  });

  const [mabTradesSorting, setMabTradesSorting] = useState(() => {
    const savedSorting = localStorage.getItem("mabTradesTableSorting");
    return savedSorting ? JSON.parse(savedSorting) : [];
  });

  const [mabTradesScrollPosition, setMabTradesScrollPosition] = useState(() => {
    return (
      parseInt(localStorage.getItem("mabTradesTableScrollPosition"), 10) || 0
    );
  });

  const [mabPnLScrollPosition, setMabPnLScrollPosition] = useState(() => {
    const saved = localStorage.getItem("mabPnLTableScrollPosition");
    return saved ? parseInt(saved, 10) : 0;
  });

  // Table states
  const [expandedPnLRows, setExpandedPnLRows] = useState(() => {
    checkAndClearStorageIfNewDay();
    const saved = localStorage.getItem("mabPnLTableExpandedRows");
    return saved ? JSON.parse(saved) : {};
  });

  const [expandedStrategyPnLRows, setExpandedStrategyPnLRows] = useState(() => {
    const saved = localStorage.getItem("mabStrategyPnLTableExpandedRows");
    return saved ? JSON.parse(saved) : {};
  });

  const [expandedSecurityPnLRows, setExpandedSecurityPnLRows] = useState(() => {
    const saved = localStorage.getItem("mabSecurityPnLTableExpandedRows");
    return saved ? JSON.parse(saved) : {};
  });

  // ===== CONFIG =====
  const tabs = [
    { value: "tab1", label: "MAB P&L" },
    { value: "tab2", label: "Trades" },
    { value: "tab3", label: "Positions" },
    { value: "tab4", label: "Arb Monitor" },
    { value: "tab5", label: "PNL (Ethan)" },
    { value: "tab6", label: "Trades (Ethan)" },
    // { value: "tab7", label: "Analysis" },
  ];

  // ===== MEMOIZED VALUES =====
  const filteredPositions = useMemo(() => {
    return allData?.filter(
      (position) => position.StrategyType === "Merger Arbitrage",
    );
  }, [allData]);

  // ===== EFFECTS =====
  useEffect(() => {
    if (datasets?.positions) setAllData(datasets.positions);
    if (datasets?.arbMonitorData) setArbMonitorData(datasets.arbMonitorData);
  }, [datasets]);

  useEffect(() => {
    const handleScroll = () => {
      localStorage.setItem(`${activeTab}ScrollPosition`, window.pageYOffset);
    };

    window.addEventListener("scroll", handleScroll);

    return () => window.removeEventListener("scroll", handleScroll);
  }, [activeTab]);

  useEffect(() => {
    const storedScrollPosition =
      parseInt(localStorage.getItem(`${activeTab}ScrollPosition`), 10) || 0;
    setTimeout(() => {
      window.scrollTo(0, storedScrollPosition);
    }, 100); // Delay to ensure content is rendered
  }, [activeTab]);

  useEffect(() => {
    if (allData?.length > 0) {
      // Filter for Merger Arbitrage positions
      const maPositions = allData?.filter(
        (position) =>
          (position.StrategyType === "Merger Arbitrage" ||
            position.StrategyType === "Merger  Arbitrage") &&
          (position.total_pnl < -100 || position.total_pnl > 100),
      );
      setMaPositions(maPositions);

      // Filter for positions with trades
      const positionsWithTrades = allData?.filter(
        (position) =>
          (position.buy_trade_quantity ||
            position.sell_trade_quantity ||
            position.short_trade_quantity ||
            position.cover_trade_quantity) &&
          (position.StrategyType === "Merger Arbitrage" ||
            position.StrategyType === "Merger  Arbitrage"),
      );
      setTradedData(positionsWithTrades);
    }
  }, [allData]);

  useEffect(() => {
    localStorage.setItem(
      "mabTradesTableGlobalFilter",
      mabTradesGlobalFilter === undefined ? "" : mabTradesGlobalFilter,
    );
  }, [mabTradesGlobalFilter]);

  useEffect(() => {
    localStorage.setItem("positionsFilter", positionsFilter);
  }, [positionsFilter]);

  useEffect(() => {
    const saveScrollPositions = () => {
      if (positionsTableContainerRef.current) {
        localStorage.setItem(
          "positionsTableScrollTop",
          positionsTableContainerRef.current.scrollTop,
        );
      }
      if (arbMonitorTableContainerRef.current) {
        localStorage.setItem(
          "arbMonitorTableScrollTop",
          arbMonitorTableContainerRef.current.scrollTop,
        );
      }
      if (tradesTableEthanContainerRef.current) {
        localStorage.setItem(
          "tradesTableEthanScrollTop",
          tradesTableEthanContainerRef.current.scrollTop,
        );
      }
    };

    window.addEventListener("beforeunload", saveScrollPositions);
    return () =>
      window.removeEventListener("beforeunload", saveScrollPositions);
  }, []);

  useEffect(() => {
    const restoreScrollPositions = () => {
      if (positionsTableContainerRef.current) {
        const savedScrollTop = localStorage.getItem("positionsTableScrollTop");
        if (savedScrollTop) {
          positionsTableContainerRef.current.scrollTop =
            parseInt(savedScrollTop);
        }
      }
      if (arbMonitorTableContainerRef.current) {
        const savedScrollTop = localStorage.getItem("arbMonitorTableScrollTop");
        if (savedScrollTop) {
          arbMonitorTableContainerRef.current.scrollTop =
            parseInt(savedScrollTop);
        }
      }
      if (tradesTableEthanContainerRef.current) {
        const savedScrollTop = localStorage.getItem(
          "tradesTableEthanScrollTop",
        );
        if (savedScrollTop) {
          tradesTableEthanContainerRef.current.scrollTop =
            parseInt(savedScrollTop);
        }
      }
    };

    const timeoutId = setTimeout(restoreScrollPositions, 100);
    return () => clearTimeout(timeoutId);
  }, [maPositions, tradedData, filteredPositions]);

  useEffect(() => {
    const container = document.querySelector(
      '[class*="MuiTableContainer-root"]',
    );
    if (container) {
      container.addEventListener("scroll", () => {
        const scrollTop = container.scrollTop;
        setMabTradesScrollPosition(scrollTop);
        localStorage.setItem("mabTradesTableScrollPosition", scrollTop);
      });
    }
    return () => {
      if (container) {
        container.removeEventListener("scroll", () => {});
      }
    };
  }, []);

  useEffect(() => {
    const val = localStorage.getItem("mabTradesTableGlobalFilter");
    if (val === "undefined") {
      localStorage.setItem("mabTradesTableGlobalFilter", "");
      setMabTradesGlobalFilter("");
    }
  }, []);

  useEffect(() => {
    localStorage.setItem(
      "mabTradesTableSorting",
      JSON.stringify(mabTradesSorting),
    );
  }, [mabTradesSorting]);

  useEffect(() => {
    if (activeTab === "tab2") {
      setTimeout(() => {
        const container = document.querySelector(
          '[class*="MuiTableContainer-root"]',
        );
        if (container) {
          const savedScroll =
            parseInt(
              localStorage.getItem("mabTradesTableScrollPosition"),
              10,
            ) || 0;
          container.scrollTop = savedScroll;
        }
      }, 200); // Delay to ensure table is rendered
    }
  }, [activeTab]);

  useEffect(() => {
    if (activeTab === "tab2") {
      const container = document.querySelector(
        '[class*="MuiTableContainer-root"]',
      );
      if (container) {
        const handleScroll = () => {
          setMabTradesScrollPosition(container.scrollTop);
          localStorage.setItem(
            "mabTradesTableScrollPosition",
            container.scrollTop,
          );
        };
        container.addEventListener("scroll", handleScroll);
        return () => container.removeEventListener("scroll", handleScroll);
      }
    }
  }, [activeTab]);

  useEffect(() => {
    localStorage.setItem(
      "mabPositionsTableSorting",
      JSON.stringify(positionsSorting),
    );
  }, [positionsSorting]);

  useEffect(() => {
    const container = document.querySelector(
      '[class*="MuiTableContainer-root"]',
    );
    if (container) {
      const handleScroll = () => {
        setPositionsScrollPosition(container.scrollTop);
        localStorage.setItem(
          "mabPositionsTableScrollPosition",
          container.scrollTop.toString(),
        );
      };

      container.addEventListener("scroll", handleScroll);

      // Restore scroll position
      const savedScrollPosition = parseInt(
        localStorage.getItem("mabPositionsTableScrollPosition"),
        10,
      );
      if (savedScrollPosition) {
        container.scrollTop = savedScrollPosition;
      }

      return () => {
        container.removeEventListener("scroll", handleScroll);
      };
    }
  }, [activeTab === "tab3"]);

  useEffect(() => {
    localStorage.setItem(
      "mabPositionsTableGlobalFilter",
      positionsFilter === undefined ? "" : positionsFilter,
    );
  }, [positionsFilter]);

  useEffect(() => {
    if (activeTab === "tab3") {
      setTimeout(() => {
        const container = positionsTableContainerRef.current;
        if (container) {
          const savedScroll =
            parseInt(
              localStorage.getItem("mabPositionsTableScrollPosition"),
              10,
            ) || 0;
          container.scrollTop = savedScroll;
        }
      }, 200); // Delay to ensure table is rendered
    }
  }, [activeTab]);

  useEffect(() => {
    const handleScroll = () => {
      if (activeTab === "tab1") {
        localStorage.setItem("mabTab1ScrollPosition", window.pageYOffset);
      } else if (activeTab === "tab2" || activeTab === "tab3") {
        const container = document.querySelector(
          '[class*="MuiTableContainer-root"]',
        );
        if (container) {
          localStorage.setItem(
            activeTab === "tab2"
              ? "mabTab2ScrollPosition"
              : "mabTab3ScrollPosition",
            container.scrollTop,
          );
        }
      }
    };

    if (activeTab === "tab1") {
      window.addEventListener("scroll", handleScroll);
    } else {
      const container = document.querySelector(
        '[class*="MuiTableContainer-root"]',
      );
      if (container) {
        container.addEventListener("scroll", handleScroll);
      }
    }

    return () => {
      if (activeTab === "tab1") {
        window.removeEventListener("scroll", handleScroll);
      } else {
        const container = document.querySelector(
          '[class*="MuiTableContainer-root"]',
        );
        if (container) {
          container.removeEventListener("scroll", handleScroll);
        }
      }
    };
  }, [activeTab]);

  useEffect(() => {
    const restoreScrollPosition = () => {
      if (activeTab === "tab1") {
        const storedPosition =
          parseInt(localStorage.getItem("mabTab1ScrollPosition"), 10) || 0;
        window.scrollTo(0, storedPosition);
      } else {
        const storedPosition =
          parseInt(
            localStorage.getItem(
              activeTab === "tab2"
                ? "mabTab2ScrollPosition"
                : "mabTab3ScrollPosition",
            ),
            10,
          ) || 0;

        setTimeout(() => {
          const container = document.querySelector(
            '[class*="MuiTableContainer-root"]',
          );
          if (container) {
            container.scrollTop = storedPosition;
          }
        }, 100);
      }
    };

    restoreScrollPosition();
  }, [activeTab]);

  useEffect(() => {
    const val = localStorage.getItem("mabTradesTableGlobalFilter");
    if (val === "undefined") {
      localStorage.setItem("mabTradesTableGlobalFilter", "");
      setMabTradesGlobalFilter("");
    }
  }, []);

  useEffect(() => {
    localStorage.setItem("mabPnLTableScrollPosition", mabPnLScrollPosition);
  }, [mabPnLScrollPosition]);

  useEffect(() => {
    if (activeTab === "tab1") {
      setTimeout(() => {
        const container = document.querySelector(
          '[class*="MuiTableContainer-root"]',
        );
        if (container) {
          // Restore scroll position
          const savedScroll =
            parseInt(localStorage.getItem("mabPnLTableScrollPosition"), 10) ||
            0;
          container.scrollTop = savedScroll;

          // Save scroll position on scroll
          const handleScroll = () => {
            setMabPnLScrollPosition(container.scrollTop);
          };
          container.addEventListener("scroll", handleScroll);
          return () => container.removeEventListener("scroll", handleScroll);
        }
      }, 200);
    }
  }, [activeTab]);

  // ===== TABLE CONFIGS =====
  const pnl_table = useMaterialReactTable({
    columns: getPnLTableColumns(theme),
    data: maPositions,
    enableColumnDragging: false,
    enableGrouping: true,
    enableSorting: true,
    enablePagination: false,
    enableStickyHeader: true,
    enableStickyFooter: true, // Ensures footer sticks to the bottom
    enableColumnResizing: true,
    enableBottomToolbar: false,
    enablePinning: true,
    columnResizeMode: "onChange",
    groupedColumnMode: "remove",
    enableTopToolbar: false,
    enableColumnActions: false,
    initialState: {
      grouping: ["StrategyName"],
      sorting: [{ id: "total_pnl", desc: true }],
      density: "compact",
      expanded: expandedPnLRows,
      columnPinning: isDesktop
        ? { left: ["StrategyName"] } // Pin StrategyName on desktop
        : {}, // No columns pinned in mobile mode
    },
    state: {
      expanded: expandedPnLRows,
    },
    onExpandedChange: (updater) => {
      const newExpanded =
        typeof updater === "function" ? updater(expandedPnLRows) : updater;
      setExpandedPnLRows(newExpanded);
      localStorage.setItem(
        "mabPnLTableExpandedRows",
        JSON.stringify(newExpanded),
      );
    },
    displayColumnDefOptions: {
      "mrt-row-expand": {
        Cell: ({ row }) => (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              paddingLeft: `${row.depth * 2}rem`,
              cursor: "pointer",
            }}
            onClick={row.getToggleExpandedHandler()}
          >
            {row.getIsExpanded() ? (
              <RemoveIcon sx={{ fontSize: { xs: "14px", sm: "16px" } }} /> // Responsive icon size
            ) : (
              <AddIcon sx={{ fontSize: { xs: "14px", sm: "16px" } }} />
            )}
            {row.getIsGrouped() && (
              <span
                style={{
                  marginLeft: "8px",
                  fontWeight: "bold",
                  textAlign: "left",
                  fontSize: { xs: "0.75rem", sm: "0.875rem" }, // Responsive font size
                }}
              >
                {row.groupingValue}
              </span>
            )}
          </div>
        ),
      },
    },
    muiTableHeadCellProps: {
      ...getCommonHeadCellProps(theme),
    },
    muiTableBodyCellProps: {
      ...getCommonBodyCellProps(theme),
    },
    muiTableBodyRowProps: {
      sx: {
        // Default background for even rows
        backgroundColor: theme.palette.background.paper,
        padding: "0px",
        height: {
          xs: "30px", // Taller rows for mobile readability
          sm: "25px",
        },
        transition: "background-color 0.2s ease",
        "&:hover": {
          backgroundColor: theme.palette.grey[400], // Hover effect for all rows
        },
        "&:nth-of-type(odd)": {
          backgroundColor: theme.palette.action.hover,
        },
      },
    },
    muiTableContainerProps: {
      sx: {
        maxHeight: {
          xs: "calc(100vh - 200px)",
          sm: "calc(100vh - 135px)", md: "calc(100vh - 105px)",
        },
        marginBottom: "0px",
        width: "100%", // Full width for responsiveness
        maxWidth: "100%", // Prevent overflow
        overflowX: "auto", // Allow horizontal scroll
        overflowY: "auto", // Ensure footer visibility
        position: "relative",
        borderRadius: 0,
        boxShadow: theme.shadows[1],
        "&::-webkit-scrollbar": {
          height: "8px",
          width: "8px", // Added for vertical scrollbar
        },
        "&::-webkit-scrollbar-track": {
          backgroundColor: theme.palette.grey[100],
          borderRadius: 0,
        },
        "&::-webkit-scrollbar-thumb": {
          backgroundColor: theme.palette.grey[300],
          borderRadius: 0,
          "&:hover": {
            backgroundColor: theme.palette.grey[400],
          },
        },
      },
    },
    muiTableProps: {
      sx: {
        width: "100%", // Use full width instead of minWidth
        tableLayout: "auto", // Allow dynamic column sizing
        "& .MuiTableHead-root": {
          borderBottom: `2px solid ${theme.palette.divider}`,
        },
        "& .MuiTableHead-root .MuiTableRow-root": {
          height: "auto",
          minHeight: {
            xs: "30px", // Taller header for mobile
            sm: "35px",
          },
        },
        "& .MuiTableHead-root .MuiTableCell-root": {
          padding: {
            xs: theme.spacing(0.5, 0.25, 0.25, 0.25),
            sm: theme.spacing(0.75, 0.5, 0.25, 0.5),
          },
          "& .Mui-TableHeadCell-Content": {
            width: "100%",
            maxWidth: "100%",
            whiteSpace: "normal !important",
            textOverflow: "unset",
            display: "flex",
            alignItems: "center",
          },
          "& .Mui-TableHeadCell-Content-Labels": {
            width: "100%",
            whiteSpace: "normal !important",
            textOverflow: "unset",
          },
          "& .Mui-TableHeadCell-Content-Wrapper": {
            whiteSpace: "normal !important",
            textOverflow: "unset",
            overflow: "visible",
          },
        },
        "& .MuiTableFooter-root": {
          borderTop: `2px solid ${theme.palette.divider}`,
          position: "sticky",
          bottom: 0,
          zIndex: 100, // Increased for visibility
          backgroundColor: theme.palette.primary.dark,
        },
        "& .MuiTableFooter-root .MuiTableRow-root": {
          height: "40px",
        },
        "& .MuiTableFooter-root .MuiTableCell-root": {
          padding: {
            xs: theme.spacing(1, 0.5), // Reduced for mobile
            sm: theme.spacing(1.5, 1),
          },
          lineHeight: "1.2",
          backgroundColor: theme.palette.primary.dark,
          fontSize: {
            xs: "0.75rem",
            sm: "0.875rem",
          },
          color: theme.palette.primary.contrastText,
        },
      },
    },
  });
  const pnl_table_Ethan1 = useMaterialReactTable({
    columns: getPnlTableColumnsEthan1(theme),
    data: maPositions,
    enableColumnDragging: false,
    enableGrouping: true,
    enableSorting: true,
    enablePagination: false,
    enableStickyHeader: true,
    enableStickyFooter: true, // Ensures footer sticks to the bottom
    enableColumnResizing: true,
    enableBottomToolbar: false,
    enablePinning: true,
    columnResizeMode: "onChange",
    groupedColumnMode: "remove",
    enableTopToolbar: false,
    enableColumnActions: false,
    initialState: {
      grouping: ["StrategyName"],
      sorting: [{ id: "total_pnl", desc: true }],
      density: "compact",
      expanded: expandedStrategyPnLRows,
      columnPinning: isDesktop
        ? { left: ["StrategyName"] } // Pin StrategyName on desktop
        : {}, // No columns pinned in mobile mode
    },
    state: {
      expanded: expandedStrategyPnLRows,
    },
    onExpandedChange: (updater) => {
      const newExpanded =
        typeof updater === "function"
          ? updater(expandedStrategyPnLRows)
          : updater;
      setExpandedStrategyPnLRows(newExpanded);
      localStorage.setItem(
        "mabStrategyPnLTableExpandedRows",
        JSON.stringify(newExpanded),
      );
    },
    displayColumnDefOptions: {
      "mrt-row-expand": {
        Cell: ({ row }) => (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              paddingLeft: theme.spacing(row.depth * 2),
              gap: 1,
              cursor: "pointer",
            }}
            onClick={row.getToggleExpandedHandler()}
          >
            {row.getIsExpanded() ? (
              <RemoveIcon sx={{ fontSize: { xs: 14, sm: 16 } }} /> // Responsive icon size
            ) : (
              <AddIcon sx={{ fontSize: { xs: 14, sm: 16 } }} />
            )}
            {row.getIsGrouped() && (
              <Typography
                variant="body2"
                sx={{
                  fontWeight: "bold",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  fontSize: { xs: "0.65rem", sm: "0.75rem" }, // Responsive font size
                }}
              >
                {row.groupingValue}
              </Typography>
            )}
          </Box>
        ),
      },
    },
    muiTableHeadCellProps: {
      ...getCommonHeadCellProps(theme),
    },
    muiTableBodyCellProps: {
      ...getCommonBodyCellProps(theme),
    },
    muiTableBodyRowProps: {
      sx: {
        // Default background for even rows
        backgroundColor: theme.palette.background.paper,
        padding: "0px",
        height: {
          xs: "30px", // Taller rows for mobile readability
          sm: "25px",
        },
        transition: "background-color 0.2s ease",
        "&:hover": {
          backgroundColor: theme.palette.grey[400], // Hover effect for all rows
        },
        "&:nth-of-type(odd)": {
          backgroundColor: theme.palette.action.hover,
        },
      },
    },
    muiTableContainerProps: {
      sx: {
        maxHeight: {
          xs: "calc(100vh - 200px)",
          sm: "calc(100vh - 135px)", md: "calc(100vh - 105px)",
        },
        marginBottom: "0px",
        width: "100%", // Full width for responsiveness
        maxWidth: "100%", // Prevent overflow
        overflowX: "auto", // Allow horizontal scroll
        overflowY: "auto", // Ensure footer visibility
        position: "relative",
        borderRadius: 0,
        boxShadow: theme.shadows[1],
        "&::-webkit-scrollbar": {
          height: "8px",
          width: "8px", // Added for vertical scrollbar
        },
        "&::-webkit-scrollbar-track": {
          backgroundColor: theme.palette.grey[100],
          borderRadius: 0,
        },
        "&::-webkit-scrollbar-thumb": {
          backgroundColor: theme.palette.grey[300],
          borderRadius: 0,
          "&:hover": {
            backgroundColor: theme.palette.grey[400],
          },
        },
      },
    },
    muiTableProps: {
      sx: {
        width: "100%", // Use full width instead of minWidth
        tableLayout: "auto", // Allow dynamic column sizing
        "& .MuiTableHead-root": {
          borderBottom: `2px solid ${theme.palette.divider}`,
        },
        "& .MuiTableHead-root .MuiTableRow-root": {
          height: "auto",
          minHeight: {
            xs: "30px", // Taller header for mobile
            sm: "35px",
          },
        },
        "& .MuiTableHead-root .MuiTableCell-root": {
          padding: {
            xs: theme.spacing(0.5, 0.25, 0.25, 0.25),
            sm: theme.spacing(0.75, 0.5, 0.25, 0.5),
          },
          "& .Mui-TableHeadCell-Content": {
            width: "100%",
            maxWidth: "100%",
            whiteSpace: "normal !important",
            textOverflow: "unset",
            display: "flex",
            alignItems: "center",
          },
          "& .Mui-TableHeadCell-Content-Labels": {
            width: "100%",
            whiteSpace: "normal !important",
            textOverflow: "unset",
          },
          "& .Mui-TableHeadCell-Content-Wrapper": {
            whiteSpace: "normal !important",
            textOverflow: "unset",
            overflow: "visible",
          },
        },
        "& .MuiTableFooter-root": {
          borderTop: `2px solid ${theme.palette.divider}`,
          position: "sticky",
          bottom: 0,
          zIndex: 100, // Increased for visibility
          backgroundColor: theme.palette.primary.dark,
        },
        "& .MuiTableFooter-root .MuiTableRow-root": {
          height: "40px",
        },
        "& .MuiTableFooter-root .MuiTableCell-root": {
          padding: {
            xs: theme.spacing(1, 0.5), // Reduced for mobile
            sm: theme.spacing(1.5, 1),
          },
          lineHeight: "1.2",
          backgroundColor: theme.palette.primary.dark,
          fontSize: {
            xs: "0.75rem",
            sm: "0.875rem",
          },
          color: theme.palette.primary.contrastText,
        },
      },
    },
  });
  const pnl_table_Ethan2 = useMaterialReactTable({
    columns: getPnlTableColumnsEthan1(theme),
    data: maPositions,
    enableColumnDragging: false,
    enableGrouping: true,
    enableSorting: true,
    enablePagination: false,
    enableStickyHeader: true,
    enableStickyFooter: true, // Ensures footer sticks to the bottom
    enableColumnResizing: true,
    enableBottomToolbar: false,
    enablePinning: true,
    columnResizeMode: "onChange",
    groupedColumnMode: "remove",
    enableTopToolbar: false,
    enableColumnActions: false,
    initialState: {
      grouping: ["StrategyName"],
      sorting: [{ id: "total_pnl", desc: false }],
      density: "compact",
      expanded: expandedSecurityPnLRows,
      // columnPinning: isDesktop
      //   ? { left: ["StrategyName"] } // Pin StrategyName on desktop
      //   : {}, // No columns pinned in mobile mode
    },
    state: {
      expanded: expandedSecurityPnLRows,
    },
    onExpandedChange: (updater) => {
      const newExpanded =
        typeof updater === "function"
          ? updater(expandedSecurityPnLRows)
          : updater;
      setExpandedSecurityPnLRows(newExpanded);
      localStorage.setItem(
        "mabSecurityPnLTableExpandedRows",
        JSON.stringify(newExpanded),
      );
    },
    displayColumnDefOptions: {
      "mrt-row-expand": {
        Cell: ({ row }) => (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              paddingLeft: theme.spacing(row.depth * 2),
              gap: 1,
              cursor: "pointer",
            }}
            onClick={row.getToggleExpandedHandler()}
          >
            {row.getIsExpanded() ? (
              <RemoveIcon sx={{ fontSize: { xs: 14, sm: 16 } }} /> // Responsive icon size
            ) : (
              <AddIcon sx={{ fontSize: { xs: 14, sm: 16 } }} />
            )}
            {row.getIsGrouped() && (
              <Typography
                variant="body2"
                sx={{
                  fontWeight: "bold",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  fontSize: { xs: "0.65rem", sm: "0.75rem" }, // Responsive font size
                }}
              >
                {row.groupingValue}
              </Typography>
            )}
          </Box>
        ),
      },
    },
    muiTableHeadCellProps: {
      ...getCommonHeadCellProps(theme),
    },
    muiTableBodyCellProps: {
      ...getCommonBodyCellProps(theme),
    },
    muiTableBodyRowProps: {
      sx: {
        // Default background for even rows
        backgroundColor: theme.palette.background.paper,
        padding: "0px",
        height: {
          xs: "30px", // Taller rows for mobile readability
          sm: "25px",
        },
        transition: "background-color 0.2s ease",
        "&:hover": {
          backgroundColor: theme.palette.grey[400], // Hover effect for all rows
        },
        "&:nth-of-type(odd)": {
          backgroundColor: theme.palette.action.hover,
        },
      },
    },
    muiTableContainerProps: {
      sx: {
        maxHeight: {
          xs: "calc(100vh - 200px)",
          sm: "calc(100vh - 135px)", md: "calc(100vh - 105px)",
        },
        marginBottom: "0px",
        width: "100%", // Full width for responsiveness
        maxWidth: "100%", // Prevent overflow
        overflowX: "auto", // Allow horizontal scroll
        overflowY: "auto", // Ensure footer visibility
        position: "relative",
        borderRadius: 0,
        boxShadow: theme.shadows[1],
        "&::-webkit-scrollbar": {
          height: "8px",
          width: "8px", // Added for vertical scrollbar
        },
        "&::-webkit-scrollbar-track": {
          backgroundColor: theme.palette.grey[100],
          borderRadius: 0,
        },
        "&::-webkit-scrollbar-thumb": {
          backgroundColor: theme.palette.grey[300],
          borderRadius: 0,
          "&:hover": {
            backgroundColor: theme.palette.grey[400],
          },
        },
      },
    },
    muiTableProps: {
      sx: {
        width: "100%", // Use full width instead of minWidth
        tableLayout: "auto", // Allow dynamic column sizing
        "& .MuiTableHead-root": {
          borderBottom: `2px solid ${theme.palette.divider}`,
        },
        "& .MuiTableHead-root .MuiTableRow-root": {
          height: "auto",
          minHeight: {
            xs: "30px", // Taller header for mobile
            sm: "35px",
          },
        },
        "& .MuiTableHead-root .MuiTableCell-root": {
          padding: {
            xs: theme.spacing(0.5, 0.25, 0.25, 0.25),
            sm: theme.spacing(0.75, 0.5, 0.25, 0.5),
          },
          "& .Mui-TableHeadCell-Content": {
            width: "100%",
            maxWidth: "100%",
            whiteSpace: "normal !important",
            textOverflow: "unset",
            display: "flex",
            alignItems: "center",
          },
          "& .Mui-TableHeadCell-Content-Labels": {
            width: "100%",
            whiteSpace: "normal !important",
            textOverflow: "unset",
          },
          "& .Mui-TableHeadCell-Content-Wrapper": {
            whiteSpace: "normal !important",
            textOverflow: "unset",
            overflow: "visible",
          },
        },
        "& .MuiTableFooter-root": {
          borderTop: `2px solid ${theme.palette.divider}`,
          position: "sticky",
          bottom: 0,
          zIndex: 100, // Increased for visibility
          backgroundColor: theme.palette.primary.dark,
        },
        "& .MuiTableFooter-root .MuiTableRow-root": {
          height: "40px",
        },
        "& .MuiTableFooter-root .MuiTableCell-root": {
          padding: {
            xs: theme.spacing(1, 0.5), // Reduced for mobile
            sm: theme.spacing(1.5, 1),
          },
          lineHeight: "1.2",
          backgroundColor: theme.palette.primary.dark,
          fontSize: {
            xs: "0.75rem",
            sm: "0.875rem",
          },
          color: theme.palette.primary.contrastText,
        },
      },
    },
  });
  const trades_table_ethan = useMaterialReactTable({
    columns: getTradeColumnsEthan(theme),
    data: tradedData,
    enableColumnDragging: false,
    enableSorting: true,
    enablePagination: false,
    enableStickyHeader: true,
    enableStickyFooter: true, // Ensures footer sticks to the bottom
    enableColumnResizing: true,
    enableBottomToolbar: false,
    enableTopToolbar: false,
    enableColumnActions: false,
    enablePinning: true,
    columnResizeMode: "onChange",
    groupedColumnMode: "remove",
    initialState: {
      sorting: [{ id: "trading_pnl", desc: true }],
      density: "compact",
      columnPinning: isDesktop
        ? { left: ["symbol"] } // Pin 'symbol' on desktop (adjust if needed)
        : {}, // No columns pinned in mobile mode
    },
    muiTableHeadCellProps: {
      ...getCommonHeadCellProps(theme),
    },
    muiTableBodyCellProps: {
      ...getCommonBodyCellProps(theme),
    },
    muiTableBodyRowProps: {
      sx: {
        // Default background for even rows
        backgroundColor: theme.palette.background.paper,
        padding: "0px",
        height: {
          xs: "30px", // Taller rows for mobile readability
          sm: "25px",
        },
        transition: "background-color 0.2s ease",
        "&:hover": {
          backgroundColor: theme.palette.grey[400], // Hover effect for all rows
        },
        "&:nth-of-type(odd)": {
          backgroundColor: theme.palette.action.hover,
        },
      },
    },
    muiTableContainerProps: {
      sx: {
        maxHeight: {
          xs: "calc(100vh - 220px)",
          sm: "calc(100vh - 135px)", md: "calc(100vh - 105px)",
        },
        marginBottom: "0px",
        width: "100%", // Full width for responsiveness
        maxWidth: "100%", // Prevent overflow
        overflowX: "auto", // Allow horizontal scroll
        overflowY: "auto", // Ensure footer visibility
        position: "relative",
        borderRadius: 0,
        boxShadow: theme.shadows[1],
        "&::-webkit-scrollbar": {
          height: "8px",
          width: "8px", // Added for vertical scrollbar
        },
        "&::-webkit-scrollbar-track": {
          backgroundColor: theme.palette.grey[100],
          borderRadius: 0,
        },
        "&::-webkit-scrollbar-thumb": {
          backgroundColor: theme.palette.grey[300],
          borderRadius: 0,
          "&:hover": {
            backgroundColor: theme.palette.grey[400],
          },
        },
      },
    },
    muiTableProps: {
      sx: {
        width: "100%", // Use full width instead of minWidth
        minWidth: "100%",
        tableLayout: "auto", // Allow dynamic column sizing
        "& .MuiTableHead-root": {
          borderBottom: `2px solid ${theme.palette.divider}`,
        },
        "& .MuiTableHead-root .MuiTableRow-root": {
          height: "auto",
          minHeight: {
            xs: "30px", // Taller header for mobile
            sm: "35px",
          },
        },
        "& .MuiTableHead-root .MuiTableCell-root": {
          padding: {
            xs: theme.spacing(0.5, 0.25, 0.25, 0.25),
            sm: theme.spacing(0.75, 0.5, 0.25, 0.5),
          },
          "& .Mui-TableHeadCell-Content": {
            width: "100%",
            maxWidth: "100%",
            whiteSpace: "normal !important",
            textOverflow: "unset",
            display: "flex",
            alignItems: "center",
          },
          "& .Mui-TableHeadCell-Content-Labels": {
            width: "100%",
            whiteSpace: "normal !important",
            textOverflow: "unset",
          },
          "& .Mui-TableHeadCell-Content-Wrapper": {
            whiteSpace: "normal !important",
            textOverflow: "unset",
            overflow: "visible",
          },
        },
        "& .MuiTableFooter-root": {
          borderTop: `2px solid ${theme.palette.divider}`,
          position: "sticky",
          bottom: 0,
          zIndex: 100, // Increased for visibility
          backgroundColor: theme.palette.primary.dark,
        },
        "& .MuiTableFooter-root .MuiTableRow-root": {
          height: "40px",
        },
        "& .MuiTableFooter-root .MuiTableCell-root": {
          padding: {
            xs: theme.spacing(1, 0.5), // Reduced for mobile
            sm: theme.spacing(1.5, 1),
          },
          lineHeight: "1.2",
          backgroundColor: theme.palette.primary.dark,
          fontSize: {
            xs: "0.75rem",
            sm: "0.875rem",
          },
          color: theme.palette.primary.contrastText,
        },
      },
    },
  });

  // ===== HANDLERS =====
  const handleTabClick = (tab) => {
    setActiveTab(tab);
    localStorage.setItem("activeTab", tab); // Save active tab to localStorage
  };

  // ===== RENDER LOGIC =====
  // (No conditional rendering logic in this component)

  // ===== RETURN =====
  return (
    <Box sx={{ width: "100%", height: "100%", overflow: "hidden" }}>
      {/* Tab Navigation */}
      <CardWrapper sx={{ mb: 0, pb: 0, pt: 0 }}>
        <TabNavigation
          tabs={tabs}
          activeTab={activeTab}
          onTabChange={handleTabClick}
        />
      </CardWrapper>

      {/* Tab Content */}
      <Box sx={{ backgroundColor: theme.palette.background.paper }}>
        {activeTab === "tab1" && (
          <Section>
            <Box
              sx={{
                height: "100%",
                display: "flex",
                flexDirection: "column",
                overflow: "hidden",
                backgroundColor: theme.palette.background.paper,
                borderRadius: 0,
                boxShadow: theme.shadows[1],
              }}
            >
              <Box
                sx={{
                  width: "100%",
                  overflow: "hidden",
                }}
              >
                <MaterialReactTable table={pnl_table} />
              </Box>
            </Box>
          </Section>
        )}

        {activeTab === "tab2" && (
          <Section>
            <TradesTable
              data={tradedData}
              title="MAB Trades Today"
              globalFilter={mabTradesGlobalFilter}
              setGlobalFilter={setMabTradesGlobalFilter}
              sorting={mabTradesSorting}
              setSorting={setMabTradesSorting}
              scrollPosition={mabTradesScrollPosition}
            />
          </Section>
        )}

        {activeTab === "tab3" && (
          <Section>
            <PositionsTable
              data={filteredPositions}
              title="Merger Arbitrage Positions"
              globalFilter={positionsFilter}
              setGlobalFilter={setPositionsFilter}
              sorting={positionsSorting}
              setSorting={setPositionsSorting}
              scrollPosition={positionsScrollPosition}
            />
          </Section>
        )}

        {activeTab === "tab4" && (
          <Section>
            <Box
              sx={{
                width: "100%",
                height: "calc(100vh - 100px)",
                overflow: "hidden",
                display: "flex",
                flexDirection: "column",
              }}
            >
              <ArbMonitor arb_monitor_data={arb_monitor_data || []} />
            </Box>
          </Section>
        )}

        {activeTab === "tab5" && (
          <Section>
            <Box
              sx={{
                height: "100%",
                display: "flex",
                flexDirection: "column",
                overflow: "hidden",
                backgroundColor: theme.palette.background.paper,
                borderRadius: 0,
                boxShadow: theme.shadows[1],
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "row",
                  overflowY: "hidden",
                  gap: 0,
                }}
              >
                <Box>
                  <MaterialReactTable table={pnl_table_Ethan1} />
                </Box>
                <Box>
                  <MaterialReactTable table={pnl_table_Ethan2} />
                </Box>
              </Box>
            </Box>
          </Section>
        )}

        {activeTab === "tab6" && (
          <Section>
            <Box
              sx={{
                height: "100%",
                display: "flex",
                flexDirection: "column",
                overflow: "hidden",
                backgroundColor: theme.palette.background.paper,
                borderRadius: 0,
                boxShadow: theme.shadows[1],
              }}
            >
              <MaterialReactTable table={trades_table_ethan} />
            </Box>
          </Section>
        )}
        {/*{activeTab === "tab7" && (*/}
        {/*  <div sx={{ width: "100%", height: "600px" }}>*/}
        {/*    <SunburstChart data={maPositions} />*/}
        {/*  </div>*/}
        {/*)}*/}
      </Box>
    </Box>
  );
};

export default MAB_PNL;
